import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ShieldCheck, Clock3, MessagesSquare, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";

const initialForm = {
  full_name: "", company_name: "", email: "", phone: "", country: "",
  product_required: "", specifications: "", quantity: "", destination: "",
  delivery_terms: "", additional_info: "",
};

const inputCls =
  "bg-midnight border-[#1E293B] text-[#F4F6F8] placeholder:text-[#94A3B8]/50 focus-visible:ring-gold/60 h-12 rounded-sm";

export const RFQSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > 10 * 1024 * 1024) {
      toast.error(t.rfq.fileTooBig);
      e.target.value = "";
      return;
    }
    setFile(f || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("file", file);
      await api.post("/rfq", data);
      toast.success(t.rfq.success);
      setForm(initialForm);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.rfq.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rfq" data-testid="rfq-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{t.rfq.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] max-w-2xl mb-14">
            {t.rfq.title}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <form
              data-testid="rfq-form"
              onSubmit={submit}
              className="bg-surface border border-[#1E293B] p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div>
                <Label htmlFor="rfq-full-name" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.fullName}</Label>
                <Input id="rfq-full-name" data-testid="rfq-full-name-input" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder={t.rfq.placeholders.fullName} />
              </div>
              <div>
                <Label htmlFor="rfq-company" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.company}</Label>
                <Input id="rfq-company" data-testid="rfq-company-input" required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder={t.rfq.placeholders.company} />
              </div>
              <div>
                <Label htmlFor="rfq-email" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.email}</Label>
                <Input id="rfq-email" data-testid="rfq-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={t.rfq.placeholders.email} />
              </div>
              <div>
                <Label htmlFor="rfq-phone" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.phone}</Label>
                <Input id="rfq-phone" data-testid="rfq-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={t.rfq.placeholders.phone} />
              </div>
              <div>
                <Label htmlFor="rfq-country" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.country}</Label>
                <Input id="rfq-country" data-testid="rfq-country-input" required value={form.country} onChange={set("country")} className={inputCls} placeholder={t.rfq.placeholders.country} />
              </div>
              <div>
                <Label htmlFor="rfq-product" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.product}</Label>
                <Input id="rfq-product" data-testid="rfq-product-input" required value={form.product_required} onChange={set("product_required")} className={inputCls} placeholder={t.rfq.placeholders.product} />
              </div>
              <div>
                <Label htmlFor="rfq-quantity" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.quantity}</Label>
                <Input id="rfq-quantity" data-testid="rfq-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder={t.rfq.placeholders.quantity} />
              </div>
              <div>
                <Label htmlFor="rfq-destination" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.destination}</Label>
                <Input id="rfq-destination" data-testid="rfq-destination-input" value={form.destination} onChange={set("destination")} className={inputCls} placeholder={t.rfq.placeholders.destination} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.terms}</Label>
                <Select value={form.delivery_terms} onValueChange={(v) => setForm({ ...form, delivery_terms: v })}>
                  <SelectTrigger data-testid="rfq-delivery-terms-select" className={inputCls}>
                    <SelectValue placeholder={t.rfq.placeholders.terms} />
                  </SelectTrigger>
                  <SelectContent className="bg-surface2 border-[#1E293B] text-[#F4F6F8]">
                    {t.rfq.terms.map((term, i) => (
                      <SelectItem key={term} value={term} data-testid={`rfq-term-${i + 1}`}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="rfq-specs" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.specs}</Label>
                <Textarea id="rfq-specs" data-testid="rfq-specifications-input" value={form.specifications} onChange={set("specifications")} rows={3} className={`${inputCls} h-auto`} placeholder={t.rfq.placeholders.specs} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="rfq-additional" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.additional}</Label>
                <Textarea id="rfq-additional" data-testid="rfq-additional-input" value={form.additional_info} onChange={set("additional_info")} rows={3} className={`${inputCls} h-auto`} placeholder={t.rfq.placeholders.additional} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.rfq.labels.upload}</Label>
                <input ref={fileRef} id="rfq-file" type="file" data-testid="rfq-file-input" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={onFile} />
                <label
                  htmlFor="rfq-file"
                  data-testid="rfq-file-label"
                  className="mt-1 flex items-center gap-3 border border-dashed border-[#1E293B] hover:border-gold/50 transition-colors duration-200 px-4 py-4 cursor-pointer text-sm text-[#94A3B8]"
                >
                  <Upload className="w-4 h-4 text-gold shrink-0" />
                  {file ? <span className="text-[#F4F6F8]">{file.name}</span> : t.rfq.placeholders.upload}
                </label>
              </div>
              <div className="sm:col-span-2 pt-2">
                <button
                  data-testid="rfq-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-8 py-4 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {loading ? t.rfq.submitting : t.rfq.submit}
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="space-y-5 lg:sticky lg:top-28">
              <div className="bg-surface border border-[#1E293B] p-8">
                <h3 className="text-lg font-semibold text-[#F4F6F8] mb-6">{t.rfq.nextTitle}</h3>
                {t.rfq.nextSteps.map((step, i) => (
                  <div key={i} className="flex gap-4 mb-5 last:mb-0">
                    <span className="font-display font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
              <div className="bg-surface2 border border-[#1E293B] p-8 space-y-4">
                <p className="flex items-center gap-3 text-sm text-[#94A3B8]">
                  <Clock3 className="w-4 h-4 text-gold shrink-0" /> {t.rfq.resp1}
                </p>
                <p className="flex items-center gap-3 text-sm text-[#94A3B8]">
                  <ShieldCheck className="w-4 h-4 text-gold shrink-0" /> {t.rfq.resp2}
                </p>
                <a
                  data-testid="rfq-whatsapp-link"
                  href="https://wa.me/2349068043369"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-[#F4F6F8] hover:text-gold transition-colors duration-200"
                >
                  <MessagesSquare className="w-4 h-4 text-gold shrink-0" /> {t.rfq.whatsapp}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
